/**
 * The horizontally scrolled deck.
 *
 * Two things make a rail work. The first is that it LOOKS scrollable: cards are
 * narrower than the frame so a second one always peeks, which is the deck's
 * only pointer affordance. The second is that it is reachable from the
 * keyboard — a horizontally scrollable region that no key can move is a
 * WCAG 2.1.1 failure, and it is the one the old rail shipped.
 *
 * Equal height is structural, from the shell's region grid, so the rail's
 * `minCardHeight` has NO default. A floor declared here cannot travel into a
 * card grid; a floor declared on the card is exactly how that happened before.
 */
"use client";
import { useCallback, useRef, type KeyboardEvent, type ReactElement, type ReactNode } from "react";
import {
  CRC_RAIL_CARD_WIDTH,
  CRC_RAIL_GAP,
  CRC_RAIL_INSET,
} from "./cresultcard.tokens";
import { useCresultcardViewport } from "./cresultcard.viewport";

/**
 * At Mobile the card narrows so the peek survives a 350px content column with
 * room to spare. It is a fixed step rather than a container-relative width
 * because the kit resolves every width decision through the viewport hook.
 */
const RAIL_CARD_WIDTH_MOBILE = 212;

enum RailKey {
  ArrowLeft = "ArrowLeft",
  ArrowRight = "ArrowRight",
  Home = "Home",
  End = "End",
}

const RAIL_SCROLL_BEHAVIOR = "smooth";
/** One row, N columns: the deck is a grid so every card gets the same track. */
const RAIL_AUTO_FLOW = "column";

interface CresultcardRailProps {
  readonly children: ReactNode;
  /** The accessible name of the deck, e.g. `Hotels, 12 results`. */
  readonly label: string;
  readonly cardWidth?: number;
  /** No default, ever. Only for a rail whose cards are ALL short. */
  readonly minCardHeight?: number;
}

export function CresultcardRail({
  children,
  label,
  cardWidth,
  minCardHeight,
}: CresultcardRailProps): ReactElement {
  const { isMobile } = useCresultcardViewport();
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const resolvedCardWidth =
    cardWidth ?? (isMobile ? RAIL_CARD_WIDTH_MOBILE : CRC_RAIL_CARD_WIDTH);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>): void => {
      const scroller = scrollerRef.current;
      if (scroller == null || event.target !== event.currentTarget) {
        return;
      }
      const step = resolvedCardWidth + CRC_RAIL_GAP;
      if (event.key === RailKey.ArrowRight) {
        event.preventDefault();
        scroller.scrollBy({ left: step, behavior: RAIL_SCROLL_BEHAVIOR });
        return;
      }
      if (event.key === RailKey.ArrowLeft) {
        event.preventDefault();
        scroller.scrollBy({ left: -step, behavior: RAIL_SCROLL_BEHAVIOR });
        return;
      }
      if (event.key === RailKey.Home) {
        event.preventDefault();
        scroller.scrollTo({ left: 0, behavior: RAIL_SCROLL_BEHAVIOR });
        return;
      }
      if (event.key === RailKey.End) {
        event.preventDefault();
        scroller.scrollTo({
          left: scroller.scrollWidth,
          behavior: RAIL_SCROLL_BEHAVIOR,
        });
      }
    },
    [resolvedCardWidth],
  );

  return (
    <div
      aria-label={label}
      className="cresultcard__rail"
      data-el="Cresultcard/Rail/Viewport"
      onKeyDown={handleKeyDown}
      ref={scrollerRef}
      role="group"
      style={{
        position: "relative",
        zIndex: 1,
        display: "grid",
        gridAutoFlow: RAIL_AUTO_FLOW,
        gridAutoColumns: resolvedCardWidth,
        // The floor lives on the rail's own row, never on a card: a minimum
        // declared on the card is what travelled into the card grid and left
        // inert empty plates behind.
        gridAutoRows:
          minCardHeight == null
            ? undefined
            : `minmax(${String(minCardHeight)}px, auto)`,
        alignItems: "stretch",
        gap: CRC_RAIL_GAP,
        paddingBlock: 12,
        paddingInline: CRC_RAIL_INSET,
        overflowX: "auto",
        overflowY: "hidden",
        scrollSnapType: "x mandatory",
      }}
      tabIndex={0}
    >
      {children}
    </div>
  );
}

interface CresultcardRailItemProps {
  readonly children: ReactNode;
}

/** One snap point. The card inside it stretches to the tallest sibling. */
export function CresultcardRailItem({
  children,
}: CresultcardRailItemProps): ReactElement {
  return (
    <div
      className="cresultcard__rail-card"
      data-el="Cresultcard/RailItem/Root"
      style={{
        display: "flex",
        alignItems: "stretch",
        minWidth: 0,
        scrollSnapAlign: "start",
      }}
    >
      {children}
    </div>
  );
}
